import React, { useMemo, useState, useEffect } from 'react';
import FullPanel from 'components/FullPanel/FullPanel';
import type {
  AppFragmentFragment as AppFragment,
  BoostPeakAverageRevenuePeriodsQuery,
  BoostPeakAverageRevenuePeriodsQueryVariables,
  BoostLowAverageRevenuePeriodsQuery,
  BoostLowAverageRevenuePeriodsQueryVariables,
  BoostYearlyRevenueQuery,
  BoostYearlyRevenueQueryVariables
} from 'generated/graphql';
import type { VenueWithMenus } from 'components/SingleItem/Details';
import { MenuFragment } from 'queries/Menu';
import classNames from 'classnames';
import { useQuery, useApolloClient } from '@apollo/client';
import {
  BOOST_PEAK_AVERAGE_REVENUE_PERIODS,
  BOOST_LOW_AVERAGE_REVENUE_PERIODS,
  BOOST_YEARLY_REVENUE
} from 'queries/Venue';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import BoostPeaks from './BoostPeaks';
import { SURGE_RECOMMENDATION_PERCENTAGE } from './constants';
import styles from './Boost.module.scss';
import BoostLows from './BoostLows';

interface YearlyRevenueProps {
  totalRevenue?: number | null;
  isLoading: boolean;
}

const YearlyRevenue: React.FC<YearlyRevenueProps> = ({ totalRevenue, isLoading }) => {
  const formattedTotalRevenue = totalRevenue?.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  return (
    <div className={styles.YearlyRevenueContainer}>
      {!isLoading && totalRevenue && (
        <>
          <p className={styles.TotalRevenue}>{formattedTotalRevenue}</p>
          <p>In returns so far this year from boosting</p>
        </>
      )}
      {!isLoading && totalRevenue === null && (
        <>
          <p>It looks like you haven&apos;t tried the doshii Boost (Dynamic pricing) feature.</p>
          <p>Here is what it might look like for your business</p>
        </>
      )}
      {isLoading && <p className={styles.TotalRevenue}>...</p>}
    </div>
  );
};

interface EstimatedBoostPotentialProps {
  estimatedBoost: number;
  isLoading: boolean;
}

const EstimatedBoostPotential: React.FC<EstimatedBoostPotentialProps> = ({ estimatedBoost, isLoading }) => {
  const formattedEstimatedBoost = estimatedBoost.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD'
  });

  const containerClassName = estimatedBoost
    ? classNames(styles.BoostPotentialContainer)
    : classNames(styles.BoostPotentialContainer, styles.centered);

  const estimatedBoostElement = (estimatedBoost: number, isLoading: boolean): JSX.Element => {
    if (isLoading) {
      return <p className={styles.Description}>Calculating estimated boost potential...</p>;
    }

    if (estimatedBoost === 0) {
      return <p className={styles.Description}>Your order data needs to be calculated</p>;
    }

    return (
      <>
        <p className={styles.Icon}>
          <i className="mm-trend-up" />
        </p>
        <p className={styles.Description}>Estimated boost potential this week {formattedEstimatedBoost}</p>
      </>
    );
  };

  return <div className={containerClassName}>{estimatedBoostElement(estimatedBoost, isLoading)}</div>;
};

interface Props {
  currentVenue: VenueWithMenus;
  isBoostEnabled: boolean;
  menuSurchargePublishCount: number;
}

const BoostPanel: React.FC<Props> = ({ currentVenue, isBoostEnabled, menuSurchargePublishCount }) => {
  const [estimatedBoost, setEstimatedBoost] = useState<number>(0);
  const [isSurchargeEnabled, setIsSurchargeEnabled] = useState<boolean>(false);

  const client = useApolloClient();

  useEffect(() => {
    const surchargeEnabled = currentVenue.menus?.some(({ mmId }) => {
      const cacheKey = client.cache.identify({ __typename: 'Menu', mmId });
      if (!cacheKey) {
        // eslint-disable-next-line no-console
        console.error(`Unable to identify Menu with mmId ${mmId}`);
        return false;
      }

      const menuFragment = client.readFragment({
        fragment: MenuFragment,
        fragmentName: 'MenuFragment',
        id: cacheKey
      });
      return menuFragment?.surcharge?.amount > 0;
    });
    if (surchargeEnabled === undefined) {
      setIsSurchargeEnabled(false);
      return;
    }
    setIsSurchargeEnabled(surchargeEnabled);
  }, [menuSurchargePublishCount, client, currentVenue.menus]);

  const {
    data: yearlyRevenueData,
    loading: yearlyRevenueLoading,
    error: yearlyRevenueError
  } = useQuery<BoostYearlyRevenueQuery, BoostYearlyRevenueQueryVariables>(BOOST_YEARLY_REVENUE, {
    variables: {
      locationId: currentVenue?.hashedId
    },
    skip: !isBoostEnabled
  });

  const yearlyRevenue = useMemo(() => {
    if (yearlyRevenueLoading || yearlyRevenueError || !yearlyRevenueData) {
      return undefined;
    }
    return yearlyRevenueData.boostYearlyRevenue;
  }, [yearlyRevenueData, yearlyRevenueLoading, yearlyRevenueError]);

  const {
    data: peakAverageRevenueData,
    loading: peakAverageRevenueLoading,
    error: peakAverageRevenueError
  } = useQuery<BoostPeakAverageRevenuePeriodsQuery, BoostPeakAverageRevenuePeriodsQueryVariables>(
    BOOST_PEAK_AVERAGE_REVENUE_PERIODS,
    {
      variables: {
        locationId: currentVenue?.hashedId
      },
      skip: !isBoostEnabled
    }
  );

  const peakAverageRevenuePeriods = useMemo(() => {
    if (peakAverageRevenueLoading || peakAverageRevenueError || !peakAverageRevenueData) {
      return [];
    }

    const boostAmount = peakAverageRevenueData.boostPeakAverageRevenuePeriods.reduce((acc, curr) => {
      const amount =
        curr.averageTotalNumberOfOrders * curr.averageTotalRevenueOrders * SURGE_RECOMMENDATION_PERCENTAGE * 100;
      return acc + amount;
    }, 0);
    setEstimatedBoost(Math.round(boostAmount * 100) / 100_00);

    return peakAverageRevenueData.boostPeakAverageRevenuePeriods;
  }, [peakAverageRevenueData, peakAverageRevenueLoading, peakAverageRevenueError]);

  const {
    data: lowAverageRevenueData,
    loading: lowAverageRevenueLoading,
    error: lowAverageRevenueError
  } = useQuery<BoostLowAverageRevenuePeriodsQuery, BoostLowAverageRevenuePeriodsQueryVariables>(
    BOOST_LOW_AVERAGE_REVENUE_PERIODS,
    {
      variables: {
        locationId: currentVenue?.hashedId
      },
      skip: !isBoostEnabled
    }
  );

  const lowAverageRevenuePeriods = useMemo(() => {
    if (lowAverageRevenueLoading || lowAverageRevenueError || !lowAverageRevenueData) {
      return [];
    }

    return lowAverageRevenueData.boostLowAverageRevenuePeriods;
  }, [lowAverageRevenueData, lowAverageRevenueLoading, lowAverageRevenueError]);

  const uniqueApps = useMemo((): AppFragment[] => {
    const uniqueApps =
      currentVenue?.menus.reduce<Set<AppFragment>>((acc, menu) => {
        menu.apps.forEach((app) => acc.add(app));
        return acc;
      }, new Set()) ?? new Set();
    return Array.from(uniqueApps);
  }, [currentVenue]);

  useEffect(() => {
    if (!currentVenue?.hashedId || !currentVenue.posName || yearlyRevenue === undefined || uniqueApps.length === 0) {
      return;
    }

    logAmplitudeEvent({
      name: 'view boost',
      category: AmplitudeEventCategories.BOOST,
      properties: {
        [AmplitudeEventProperties.VENUE_ID]: currentVenue.hashedId,
        [AmplitudeEventProperties.POS_NAME]: currentVenue.posName || '',
        [AmplitudeEventProperties.APPS]: uniqueApps
          .map((app) => {
            return app.id.includes('virtual') && app.brand ? `${app.name} - brand` : app.name;
          })
          .join(', '),
        [AmplitudeEventProperties.SURGE_REVENUE]: String(yearlyRevenue)
      }
    });
  }, [currentVenue?.hashedId, uniqueApps, currentVenue?.posName, yearlyRevenue]);

  return (
    <FullPanel title="Doshii Boost" route="boost">
      <YearlyRevenue totalRevenue={yearlyRevenue} isLoading={yearlyRevenueLoading} />
      <EstimatedBoostPotential estimatedBoost={estimatedBoost} isLoading={peakAverageRevenueLoading} />
      <BoostPeaks
        peakRanges={peakAverageRevenuePeriods}
        isSurchargeEnabled={isSurchargeEnabled}
        posName={currentVenue.posName || ''}
      />
      <BoostLows lowRanges={lowAverageRevenuePeriods} posName={currentVenue.posName || ''} />
      <div className={styles.Footer}>
        <p className={styles.Heading}>How are my venue peak times calculated?</p>
        <p className={styles.Description}>
          We analyse your delivery order data from the last eight weeks to determine your average orders per 30-minute
          window. Based on that, we&apos;ve handpicked your busiest periods to give your boost strategy an extra kick!
        </p>
      </div>
    </FullPanel>
  );
};

export default BoostPanel;
